/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Heading, Button, Text, Icon, CardCarousel } from 'sarsaparilla';
import { useUserLocation } from 'shared-ui/geolocation';

import { usePermitsNearbyQuery, RECOMMENDATION_RADIUS } from '../api/search-api.queries';
import { CarouselCard } from '../CarouselCard/CarouselCard';
import { HeroCarouselSkeleton } from './HeroCarouselSkeleton';
import { CarouselError } from './CarouselError';

export function PermitsCarousel() {
    const userLocation = useUserLocation();

    const { data, isError, isPending } = usePermitsNearbyQuery({
        isEnabled: userLocation !== null,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        location: userLocation?.location,
    });

    const cards = React.useMemo(() => {
        if (!data?.results) {
            return [];
        }

        return data?.results?.map((item) => {
            return (
                <CarouselCard
                    key={item.id}
                    item={item}
                    onClick={() => {}}
                    gaTrackingId="899974145790"
                    gaTrackingValue={item.name}
                    showRating={false}
                />
            );
        });
    }, [data?.results]);

    const exploreMoreHref = React.useMemo(() => {
        if (!userLocation) {
            return undefined;
        }

        return getExploreMoreHref(userLocation.latitude, userLocation.longitude);
    }, [userLocation]);

    return (
        <section>
            <Heading headingLevel={2} appearance="h6">
                Permits Near You
            </Heading>
            <Text className="mb-2">
                Closest entrance, activity, and vehicle permits.{' '}
                <Button
                    appearance="link"
                    href={exploreMoreHref}
                    isDisabled={!exploreMoreHref}
                    iconAfterElement={<Icon iconName="arrow-forward" />}
                    className="ml-half"
                    style={{ verticalAlign: 'baseline' }}
                    aria-label="Explore more nearby permits"
                    gaTrackingId="899974145790"
                >
                    Explore More
                </Button>
            </Text>

            {isPending ? (
                <HeroCarouselSkeleton />
            ) : isError || !data?.results ? (
                <CarouselError />
            ) : (
                <CardCarousel
                    accessibleName="Permits Nearby"
                    gaTrackingId="899974145790"
                    itemsPerBreakpoint={{ md: 4, xl: 5 }}
                    overscanPerBreakpoint={{ md: 12 }}
                >
                    {cards}
                </CardCarousel>
            )}
        </section>
    );
}

function getExploreMoreHref(latitude: number, longitude: number): string {
    const searchParams = new URLSearchParams({
        inventory_type: 'permits',
        sort: 'score',
        lat: latitude.toString(),
        lng: longitude.toString(),
        clear: 'true',
        radius: RECOMMENDATION_RADIUS,
    });

    return `/search?${searchParams.toString()}`;
}
